import { useMemo } from 'react';
import { onlyDateFromServer, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toString } from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IAccountOrderDto } from '@/dtos';
import { useAuthStore } from '@/modules/user/stores';

export enum ProofOfExpensesTypes {
  RECEIPT_REQUIRED = 'RECEIPT_REQUIRED',
  NO_RECEIPT_REQUIRED = 'NO_RECEIPT_REQUIRED',
  CERTIFICATION_REQUIRED = 'CERTIFICATION_REQUIRED',
}

interface CoveragePeriod {
  coverage_date_end?: string;
  coverage_date_start?: string;
}

interface AccountOrderPayload {
  account_id: number;
  order: number;
  available_balance: number;
  proof_of_expenses_type: ProofOfExpensesTypes;
  coverage_periods?: CoveragePeriod[];
  submit_claims_date_end: string;
  is_forfeited?: boolean | null;
  plan_name?: string;
}

export interface AccountOrder {
  accountId: string;
  order: number;
  availableBalance: number;
  proofOfExpensesType: ProofOfExpensesTypes;
  coverageDateStart?: string;
  coverageDateEnd?: string;
  submitClaimsDateEnd?: string;
  isForfeited?: boolean | null;
  planName?: string;
}

const formatData = (accountOrders: IAccountOrderDto[]): AccountOrder[] =>
  accountOrders
    .map((accountOrder) => ({
      accountId: toString(accountOrder.account_id),
      order: accountOrder.order || 0,
      planName: accountOrder?.plan_name,
      availableBalance: accountOrder.available_balance || 0,
      proofOfExpensesType: accountOrder.proof_of_expenses_type as unknown as ProofOfExpensesTypes,
      coverageDateStart: onlyDateFromServer(
        accountOrder?.coverage_periods?.find((item) => item?.coverage_date_start)
          ?.coverage_date_start as unknown as string
      ),
      coverageDateEnd: onlyDateFromServer(
        accountOrder?.coverage_periods?.find((item) => item?.coverage_date_end)
          ?.coverage_date_end as unknown as string
      ),
      submitClaimsDateEnd: onlyDateFromServer(accountOrder.submit_claims_date_end as unknown as string),
      isForfeited: accountOrder?.is_forfeited,
    }))
    .sort((a, b) => a.order - b.order);

interface Props {
  serviceId?: string;
  serviceOn?: string;
  dependentId?: string;
  claimValue?: number;
}

const useAccountOrdersQuery = ({ serviceId, serviceOn, dependentId, claimValue }: Props) => {
  const { user } = useAuthStore();
  const employeeId = toString(user?.employee?.id);
  const { isLoading, isSuccess, isError, data } = useQuery(
    [API_URL.ACCOUNT_ORDERS, serviceId, employeeId, serviceOn, dependentId, claimValue],
    () =>
      api.get<IAccountOrderDto[][]>(API_URL.ACCOUNT_ORDERS, {
        params: {
          service_id: serviceId,
          service_on: dayjs(serviceOn).format(DEFAULT_DATE_FORMAT),
          employee_id: employeeId,
          ...(dependentId ? { dependent_id: dependentId } : {}),
          ...(claimValue ? { claim_value: claimValue } : {}),
        },
      }),
    { enabled: Boolean(serviceId && employeeId && serviceOn) }
  );

  const accountOrders = useMemo(() => formatData(data?.data?.flat()?.flat() || []), [data]);

  const availableAccountNames = useMemo(() => {
    if (!accountOrders?.length) return [];
    const currentDate = dayjs().startOf('date').valueOf();

    const filteredAccountOrders = accountOrders
      ?.flat()
      ?.filter((accountOrder) => {
        const submitClaimsDateEnd = accountOrder?.submitClaimsDateEnd
          ? dayjs(accountOrder?.submitClaimsDateEnd).valueOf()
          : 0;
        const isClaimsDateExpired = submitClaimsDateEnd && submitClaimsDateEnd < currentDate;
        return !accountOrder?.isForfeited && accountOrder?.planName && !isClaimsDateExpired;
      })
      ?.sort((prev, next) => ((prev?.order || 0) > (next?.order || 0) ? 1 : -1))
      ?.map((accountOrder) => accountOrder?.planName) as string[];

    return [...new Set(filteredAccountOrders)];
  }, [accountOrders]);

  return {
    accountOrders,
    availableAccountNames,
    isError,
    isLoading,
    isSuccess,
  };
};

export default useAccountOrdersQuery;
